/**
 * App state: which screen is showing, the player's progression and settings, the current match
 * and the last fight's summary. Screens read from here; progression changes only through the
 * actions below. With a game server (Milestone 4) the server owns progression: fights are
 * rewarded there, Training and resets go through its API, and this browser keeps a copy. Builds
 * without one (development, some tests) keep Milestone 1's local progression.
 */
import {
  applyFight,
  availablePoints,
  botOpponent,
  buyFighter,
  buyItem,
  confirmTraining,
  DEFAULT_FIGHTER,
  equipItem,
  fighterName,
  keepLocker,
  levelOf,
  lookOf,
  newProgress,
  playableFighter,
  resetSkills,
  rollover,
  type CosmeticKind,
  type FightResult,
  type FightRewards,
  type HistoryView,
  type Progress,
  type SkillRanks,
} from '@caged/shared';
import { create } from 'zustand';
import type { MatchSetup, NetStats } from '../game/session';
import type { Account } from '../net/account';
import type { SocialReturn } from '../net/social';
import { DEV_GUEST, devGuestId, onlineConfigured, serverProgress } from '../net/config';
import { savedAccount } from '../net/savedAccount';
import type { Entry, LeagueSearch, LobbyInfo, OnlineLink, Search, ServerRewards } from '../net/online';
import type { AccountFighter } from '../net/progressApi';
import { diag } from '../platform/diag';
import { clearActiveFight, rejoinableFight, type ActiveFight } from '../net/activeFight';
import { enterFightMode } from '../platform/gameMode';
import { progressStore } from '../storage/progressStore';
import { loadSettings, saveSettings, type Settings } from '../storage/settings';

export type Screen =
  | 'home'
  | 'training'
  | 'profile'
  | 'settings'
  | 'locker'
  | 'faq'
  | 'league'
  | 'leagues'
  | 'country'
  | 'plan'
  | 'matchmaking'
  | 'vs'
  | 'fight'
  | 'result';

/** The league screens (Milestone 5): Back walks back through them. */
export type LeagueScreen = 'league' | 'leagues' | 'country' | 'plan';
export const LEAGUE_SCREENS: readonly Screen[] = ['league', 'leagues', 'country', 'plan'];

/**
 * What a league screen shows: a series by id, a country (and one of its divisions); null on
 * League is the player's own.
 */
export interface LeagueTarget {
  series?: string;
  country?: string;
  division?: number;
}

export type Popup =
  | { kind: 'levelUp'; level: number }
  | { kind: 'skillPoint'; gained: number; available: number }
  | { kind: 'weeklyBand'; fights: number; factor: number }
  | { kind: 'streak'; xp: number }
  /** A league season ended (M5): promotion, relegation, a belt, national champion (§7.9). */
  | { kind: 'season'; season: HistoryView }
  /** A guest's level or skill points, with the offer to register and keep them (D41). */
  | { kind: 'keep'; level: number | null; points: number };

export interface FightSummary {
  match: MatchSetup;
  result: FightResult;
  /**
   * What the fight earned; null when nothing was earned here: a practice fight (offline with a
   * game server, D26), or the server's rewards didn't arrive (they're on the server anyway).
   */
  rewards: FightRewards | null;
  /** Offline in a build with server progression: nothing is earned (D26). */
  practice?: boolean;
  /** Local side is always 0 in offline fights. */
  outcome: 'win' | 'loss' | 'draw';
  /** Online: lost by forfeit because this side's connection could not come back. */
  connectionLost?: boolean;
  /** Online: how the connection went. */
  net?: NetStats;
}

export function timeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function loadProgress(): Progress {
  const now = Date.now();
  const saved = progressStore.load();
  return saved ? rollover(saved, now, timeZone()) : newProgress(now, timeZone());
}


export interface Matchmaking {
  status: 'searching' | 'found' | 'offline';
  /** Date.now() when the search began. */
  since: number;
  vsBot?: boolean;
  /**
   * A league fight (M5): its lobby, once joined (who the opponent is, whether they're here), or
   * why it couldn't be joined.
   */
  league?: { fixtureId: string; lobby: LobbyInfo | null; failed?: string };
}

/** The running online search, if any (kept out of the store: it isn't state to render). */
let search: Search | null = null;
/** The league lobby, while waiting in it (it can start the fight against the game plan). */
let leagueSearch: LeagueSearch | null = null;

/** How long matchmaking waits for the connection's country (a guest's flag) before going without. */
const GEO_WAIT_MS = 1500;

/** Matchmaking, loading the online code (Supabase, Colyseus) only when it's first needed. */
function lazyFindMatch(): Search {
  let cancelled = false;
  let inner: Search | null = null;
  return {
    promise: (async () => {
      // A name or fighter just picked reaches the account first: the fight takes the account's (D55).
      await savingPick?.catch(() => undefined);
      // This browser's progress must be on the server before its first fight there (D25).
      const app = useApp.getState();
      if (serverProgress() && app.progressSync !== 'server' && !(await app.syncProgress({ create: true }))) {
        throw new Error('progress not synced');
      }
      // A guest fights under the flag of the country they connect from; the server gives a
      // registered player the country they chose instead (docs/fight.md §10).
      const { connectionCountry } = await import('../net/geo');
      const country = await Promise.race([connectionCountry(), new Promise<null>((r) => setTimeout(() => r(null), GEO_WAIT_MS))]);
      const { findMatch } = await import('../net/online');
      if (cancelled) throw new Error('cancelled');
      // The server fights an account under its own name and fighter; these only start a new one's.
      const entry = entryOf(useApp.getState());
      inner = findMatch(country ? { ...entry, country } : entry);
      return inner.promise;
    })(),
    cancel() {
      cancelled = true;
      inner?.cancel();
    },
  };
}

function entryOf(st: { progress: Progress; settings: Settings }): Entry {
  return {
    name: st.settings.playerName || 'Fighter',
    fighterId: playableFighter(st.progress, st.settings.fighterId),
    ranks: st.progress.ranks,
    level: levelOf(st.progress).level,
    timeZone: timeZone(),
  };
}

/** The popups a fight's rewards bring up. */
function popupsFor(r: FightRewards, progress: Progress, guest: boolean): Popup[] {
  const popups: Popup[] = [];
  const level = r.levelAfter.level > r.levelBefore.level ? r.levelAfter.level : null;
  // A guest gets what they won together with the offer to keep it, in one popup (D41).
  if (guest && (level !== null || r.skillPointsGained > 0)) popups.push({ kind: 'keep', level, points: r.skillPointsGained });
  else {
    if (level !== null) popups.push({ kind: 'levelUp', level });
    if (r.skillPointsGained > 0) popups.push({ kind: 'skillPoint', gained: r.skillPointsGained, available: availablePoints(progress) });
  }
  if (r.nextBandFactor !== r.bandFactor) popups.push({ kind: 'weeklyBand', fights: r.weekFights, factor: r.nextBandFactor });
  if (r.streakCompleted) popups.push({ kind: 'streak', xp: 300 });
  return popups;
}

/** Not registered: a guest account, or none yet (dev builds: a "reg-" dev player is registered). */
function isGuest(account: Account | null): boolean {
  if (DEV_GUEST) return !devGuestId().startsWith('reg-');
  return !account?.signedIn || account.guest;
}

/** Waits for the progress sync (the first one moves this browser's progress over, D25). */
let syncing: Promise<boolean> | null = null;

/**
 * A change the server makes to progression (Training, resets). On failure the copy here may be
 * out of date, so it's loaded again.
 */
async function remoteChange(fn: (api: typeof import('../net/progressApi')) => Promise<Progress>): Promise<boolean> {
  try {
    // The progress must be on the server first (a player who never played online: moved now).
    if (useApp.getState().progressSync !== 'server' && !(await useApp.getState().syncProgress({ create: true }))) return false;
    const progress = await fn(await import('../net/progressApi'));
    progressStore.save(progress);
    useApp.setState({ progress, progressSync: 'server' });
    return true;
  } catch (err) {
    diag.error(`progress: change failed: ${String(err)}`);
    void useApp.getState().syncProgress();
    return false;
  }
}

/** Changes this device's settings, and nothing on the server. */
function setLocalSettings(patch: Partial<Settings>): void {
  const settings = { ...useApp.getState().settings, ...patch };
  saveSettings(settings);
  useApp.setState({ settings });
}

/** Names and fighters picked on this device (Profile): a sync that began before one doesn't undo it. */
let picks = 0;
/** A pick on its way to the server: syncs leave the name and fighter alone, matchmaking waits for it. */
let savingPick: Promise<unknown> | null = null;

/**
 * The account's name and fighter, as the server keeps them, replace this device's copy (D55):
 * the copy only shows them before the first answer, or offline. A copy that went out of date
 * once replaced an account's name in the league tables. An account without them yet (it never
 * fought) keeps this device's, which its first fight or signup makes the account's.
 */
function takeAccountFighter(fighter: AccountFighter | null, picksBefore: number): void {
  if (!fighter || picks !== picksBefore || savingPick) return;
  const { playerName, fighterId } = useApp.getState().settings;
  if (playerName !== fighter.name || fighterId !== fighter.fighterId) setLocalSettings({ playerName: fighter.name, fighterId: fighter.fighterId });
}

/**
 * A name or fighter picked here: shown at once, and the account's on every device once the server
 * has it. Without an account yet it stays this device's (the first fight or signup takes it); when
 * the server can't be reached, the account's comes back. False then.
 */
async function pick(picked: 'name' | 'fighter', patch: Pick<Partial<Settings>, 'playerName' | 'fighterId'>): Promise<boolean> {
  const before = useApp.getState().settings;
  const mine = ++picks;
  setLocalSettings(patch);
  if (!serverProgress()) return true;
  const { settings, progress } = useApp.getState();
  const fighter = { name: settings.playerName || 'Fighter', fighterId: playableFighter(progress, settings.fighterId) };
  const save = import('../net/progressApi').then((api) => api.saveFighterRemote(fighter, picked));
  savingPick = save;
  try {
    const saved = await save;
    // As the server keeps them: the name cleaned up, and the account's other half.
    if (picks === mine) setLocalSettings({ playerName: saved.name, fighterId: saved.fighterId });
    return true;
  } catch (err) {
    if (err instanceof Error && err.name === 'NoSession') return true;
    diag.error(`profile: saving the ${picked} failed: ${String(err)}`);
    if (picks === mine) setLocalSettings({ playerName: before.playerName, fighterId: before.fighterId });
    return false;
  } finally {
    if (savingPick === save) savingPick = null;
  }
}

/** A quick fight against a local bot (M1, and the fallback when online play isn't available). */
function offlineMatch(st: { progress: Progress; settings: Settings }): MatchSetup {
  const { progress, settings } = st;
  const seed = (Math.random() * 0x7fffffff) | 0;
  const level = levelOf(progress).level;
  const fighterId = playableFighter(progress, settings.fighterId);
  const bot = botOpponent({ fighterId, ranks: progress.ranks, level }, settings.difficulty, seed);
  return {
    seed,
    arenaId: 'snow',
    player: { fighterId, name: settings.playerName || 'You', ranks: progress.ranks, level, look: lookOf(progress) },
    opponent: { ...bot, bot: { difficulty: settings.difficulty } },
    local: 0,
  };
}

/** The match as this player sees it, from what the server sent. */
function onlineMatch(link: OnlineLink): MatchSetup {
  const { you, match } = link.info;
  const me = match.fighters[you];
  const opp = match.fighters[you === 0 ? 1 : 0];
  return {
    seed: match.seed,
    arenaId: match.arenaId,
    player: { fighterId: me.fighterId, name: me.name, ranks: me.ranks, level: me.level, country: me.country, look: me.look },
    opponent: {
      fighterId: opp.fighterId,
      name: opp.name,
      ranks: opp.ranks,
      level: opp.level,
      country: opp.country,
      look: opp.look,
      bot: opp.bot ? { difficulty: opp.bot.difficulty, plan: opp.bot.plan ?? null } : undefined,
    },
    local: you,
    online: link,
  };
}

/** The same result seen from side 1. */
function mirrorResult(r: FightResult): FightResult {
  const flip = (w: 0 | 1 | null): 0 | 1 | null => (w === null ? null : w === 0 ? 1 : 0);
  return {
    ...r,
    winner: flip(r.winner),
    roundWinners: r.roundWinners.map(flip),
    damage: [r.damage[1], r.damage[0]],
    ...(r.skillDamage ? { skillDamage: [r.skillDamage[1], r.skillDamage[0]] } : {}),
  };
}

export interface AppState {
  screen: Screen;
  progress: Progress;
  settings: Settings;
  match: MatchSetup | null;
  /** The quick-fight search: online, or offline against a local bot (no server, or unreachable). */
  matchmaking: Matchmaking | null;
  lastFight: FightSummary | null;
  popups: Popup[];
  /** An online fight a reloaded page can still rejoin (D19). */
  rejoin: ActiveFight | null;
  /**
   * Where progress comes from: this browser (no game server), or the server (Milestone 4):
   * `pending` until the first answer, `failed` while it can't be reached (the copy here shows).
   */
  progressSync: 'local' | 'pending' | 'server' | 'failed';
  /**
   * Who is signed in on this device (Milestone 4): at start as its saved session says, then as
   * supabase-js says (loadAccount); null until known, or without Supabase.
   */
  account: Account | null;
  /** The sign-in sheet, when open (components/AuthSheet). */
  authSheet: 'register' | 'login' | 'signup' | null;
  /** The email the sheet starts registering with (the post-fight offer, D41). */
  authEmail: string | null;
  /** Google or Facebook's answer when the game was opened by one (net/social.ts); the sheet finishes it. */
  socialReturn: SocialReturn | null;
  /** What the league screen shows (null: the player's own league). */
  leagueTarget: LeagueTarget | null;
  /** League screens opened one from another, for Back. */
  leagueTrail: { screen: Screen; target: LeagueTarget | null }[];

  go(screen: Screen): void;
  /** Opens a league screen from wherever the player is (Back returns there). */
  openLeague(screen: LeagueScreen, target?: LeagueTarget | null): void;
  /** Back from a league screen: the one it was opened from, else Home. */
  leagueBack(): void;
  /**
   * Loads progress from the server; the first time this browser's own moves over (D25). Makes
   * the guest account first when `create` (a player's action), else only with a session.
   */
  syncProgress(options?: { create?: boolean }): Promise<boolean>;
  /** Who is signed in, read at start (makes no account). */
  loadAccount(): Promise<void>;
  /**
   * After signing in, registering or Complete signup: reloads who it is, and their progress, name
   * and fighter (another, existing account's after logging in).
   */
  accountChanged(): Promise<void>;
  /** Log out: this device starts again as a new guest. */
  logOut(): Promise<void>;
  /** `email`: register with it straight away (its code goes out as the sheet opens). */
  openAuth(mode: 'register' | 'login' | 'signup', email?: string): void;
  closeAuth(): void;
  /** Starts the quick-fight loop: matchmaking (online, or a local bot) → VS → fight. */
  startQuickFight(): void;
  /** Backs out of matchmaking (and out of a found match that hasn't started). */
  cancelMatchmaking(): void;
  /** Today's league fight (M5): into its lobby, then the fight loop as for a quick fight. */
  startLeagueFight(fixtureId: string): void;
  /** In the league lobby: fight the absent opponent's game plan now. */
  fightPlanNow(): void;
  /** Watches a league fight's replay (M5); false when it couldn't be loaded. */
  watchReplay(fightId: string, day: number): Promise<boolean>;
  /** Back from a replay to the league screen it was opened from. */
  stopWatching(): void;
  /** `server`: the rewards the server applied to an online fight (Milestone 4). */
  finishFight(
    match: MatchSetup,
    result: FightResult,
    options?: { connectionLost?: boolean; net?: NetStats; server?: ServerRewards | null },
  ): void;
  /** Back into the saved online fight; false when it can't be rejoined any more. */
  rejoinFight(): Promise<boolean>;
  /** Forgets the saved fight; `forfeit` gives it up at once (it counts as a loss either way). */
  dropRejoin(forfeit: boolean): void;
  /** False when it couldn't be saved (no connection, or the copy here was out of date). */
  confirmTraining(ranks: SkillRanks): Promise<boolean>;
  resetSkills(): Promise<boolean>;
  /**
   * The Locker (M7): buys an item with coins and puts it on, or puts on one already owned.
   * False when it couldn't be saved. The Locker checks the price, the coins and the unlock
   * first; the server checks them again.
   */
  buyItem(kind: CosmeticKind, id: string): Promise<boolean>;
  equipItem(kind: CosmeticKind, id: string): Promise<boolean>;
  /** A paid fighter (D52): buys it with coins and picks it. False when it couldn't be saved. */
  buyFighter(id: string): Promise<boolean>;
  /**
   * Profile: a new name, or a fighter the player has. They're the account's (D55), so they go to
   * the server; false when they couldn't be saved there (the account's stay).
   */
  pickName(name: string): Promise<boolean>;
  pickFighter(id: string): Promise<boolean>;
  /** This device's settings (the name and fighter are the account's: pickName, pickFighter). */
  updateSettings(patch: Partial<Omit<Settings, 'playerName' | 'fighterId'>>): void;
  dismissPopup(): void;
  /** Settings → Reset progress: back to level 1. */
  resetProgress(): Promise<boolean>;

  // --- UI (menus) -------------------------------------------------------------------------
  /**
   * Home was opened: moves the week and day counters forward (so the weekly meter resets on
   * Monday without a fight).
   */
  homeOpened(): void;
  /**
   * Gives a player without a name a made-up one (settings.playerName). An account's own name
   * replaces it at the next sync; an account without one takes it with its first fight.
   */
  ensurePlayerName(name: string): void;
}

/** Logged out: this device starts again from scratch, as a new guest. */
async function startAfresh(): Promise<void> {
  (await import('../net/progressApi')).forgetProgressSent();
  progressStore.clear();
  // The name and fighter were the account's: a new guest gets a made-up name of its own.
  picks++;
  setLocalSettings({ playerName: fighterName(Math.random), fighterId: DEFAULT_FIGHTER });
  useApp.setState({
    progress: newProgress(Date.now(), timeZone()),
    account: { email: null, guest: true, signedIn: false, provider: null },
    progressSync: 'pending',
    lastFight: null,
  });
}

export const useApp = create<AppState>((set, get) => ({
  screen: 'home',
  progress: loadProgress(),
  settings: loadSettings(),
  match: null,
  matchmaking: null,
  lastFight: null,
  popups: [],
  rejoin: rejoinableFight(),
  progressSync: serverProgress() ? 'pending' : 'local',
  account: savedAccount(),
  authSheet: null,
  authEmail: null,
  socialReturn: null,
  leagueTarget: null,
  leagueTrail: [],

  go: (screen) => set({ screen, leagueTarget: null, leagueTrail: [] }),
  openLeague: (screen, target = null) => {
    const st = get();
    const from = LEAGUE_SCREENS.includes(st.screen) ? [...st.leagueTrail, { screen: st.screen, target: st.leagueTarget }] : [{ screen: st.screen, target: null }];
    set({ screen, leagueTarget: target, leagueTrail: from.slice(-12) });
  },
  leagueBack: () => {
    const trail = get().leagueTrail;
    const prev = trail[trail.length - 1];
    if (!prev) set({ screen: 'home', leagueTarget: null, leagueTrail: [] });
    else set({ screen: prev.screen, leagueTarget: prev.target, leagueTrail: trail.slice(0, -1) });
  },
  openAuth: (mode, email) => set({ authSheet: mode, authEmail: email ?? null }),
  closeAuth: () => set({ authSheet: null, socialReturn: null, authEmail: null }),

  syncProgress: ({ create = false } = {}) => {
    if (!serverProgress()) return Promise.resolve(true);
    // One at a time; an action that may make the guest account waits for a quiet sync to end.
    const run = () => {
      const picksBefore = picks;
      return import('../net/progressApi')
        .then(({ fetchProgress }) => fetchProgress(progressStore.load(), timeZone(), create))
        .then(({ progress, fighter }) => {
          progressStore.save(progress);
          set({ progress, progressSync: 'server' });
          takeAccountFighter(fighter, picksBefore);
          return true;
        })
        .catch((err: unknown) => {
          // No session yet and none to be made (a visitor who hasn't played online): not a failure.
          if (!(err instanceof Error && err.name === 'NoSession')) {
            diag.error(`progress: sync failed: ${String(err)}`);
            set({ progressSync: get().progressSync === 'server' ? 'server' : 'failed' });
          }
          return false;
        });
    };
    const next = (syncing ?? Promise.resolve(false)).then((done) => (done && !create ? done : run()));
    syncing = next.finally(() => {
      if (syncing === next) syncing = null;
    });
    return next;
  },

  loadAccount: async () => {
    if (!serverProgress()) return;
    try {
      const { currentAccount } = await import('../net/account');
      set({ account: await currentAccount() });
    } catch {
      // No Supabase settings, or offline.
    }
  },

  accountChanged: async () => {
    if (!serverProgress()) return;
    try {
      const { currentAccount } = await import('../net/account');
      set({ account: await currentAccount() });
    } catch {
      // Offline: keep what's shown.
    }
    // Logged in to an existing account: its name and fighter replace this device's along with its
    // progress (or with a later sync, if this one fails).
    await get().syncProgress({ create: true });
  },

  logOut: async () => {
    const { signOut } = await import('../net/account');
    await signOut();
    // The account's progress stays with the account: this device starts again from scratch.
    await startAfresh();
  },

  startQuickFight: () => {
    // Called from the Quick fight / Fight again tap: a user gesture, so fullscreen is allowed.
    enterFightMode();
    search?.cancel();
    search = null;
    const since = Date.now();
    const offline = () =>
      set({ match: offlineMatch(get()), matchmaking: { status: 'offline', since }, screen: 'matchmaking' });
    if (!onlineConfigured()) {
      offline();
      return;
    }
    const s = lazyFindMatch();
    search = s;
    set({ match: null, matchmaking: { status: 'searching', since }, screen: 'matchmaking' });
    s.promise.then(
      (link) => {
        if (search !== s) {
          link.leave();
          return;
        }
        search = null;
        set({ match: onlineMatch(link), matchmaking: { status: 'found', since, vsBot: link.info.vsBot } });
      },
      (err: unknown) => {
        if (search !== s) return;
        search = null;
        // No server (or it can't be reached): the game still plays, against a local bot.
        diag.error(`online: matchmaking failed, playing offline: ${String(err)}`);
        // This build is too old for the server: get the new one (it takes over when it's calm).
        if (err instanceof Error && err.message === 'update required') void import('../platform/update').then((u) => u.checkForUpdate(true));
        if (get().screen === 'matchmaking') offline();
      },
    );
  },

  rejoinFight: async () => {
    const saved = get().rejoin;
    if (!saved) return false;
    // A tap: fullscreen and landscape where the browser allows it (Android).
    enterFightMode();
    try {
      const { rejoin } = await import('../net/online');
      const link = await rejoin(saved);
      search?.cancel();
      search = null;
      set({ rejoin: null, match: onlineMatch(link), matchmaking: null, screen: 'fight' });
      return true;
    } catch (err) {
      diag.error(`online: rejoin failed: ${String(err)}`);
      clearActiveFight();
      return false;
    }
  },

  dropRejoin: (forfeit) => {
    const saved = get().rejoin;
    clearActiveFight();
    set({ rejoin: null });
    if (saved && forfeit) void import('../net/online').then(({ forfeitSaved }) => forfeitSaved(saved));
  },

  startLeagueFight: (fixtureId) => {
    // A tap on "Fight now": fullscreen and landscape where the browser allows it (Android).
    enterFightMode();
    search?.cancel();
    const since = Date.now();
    set({ match: null, matchmaking: { status: 'searching', since, league: { fixtureId, lobby: null } }, screen: 'matchmaking' });
    const onLobby = (lobby: LobbyInfo) => {
      const mm = get().matchmaking;
      if (mm?.league?.fixtureId === fixtureId) set({ matchmaking: { ...mm, league: { ...mm.league, lobby } } });
    };
    const s: LeagueSearch = (() => {
      let inner: LeagueSearch | null = null;
      let cancelled = false;
      return {
        promise: import('../net/online').then(({ joinLeague }) => {
          if (cancelled) throw new Error('cancelled');
          inner = joinLeague(fixtureId, entryOf(get()), onLobby);
          return inner.promise;
        }),
        cancel() {
          cancelled = true;
          inner?.cancel();
        },
        fightPlan() {
          inner?.fightPlan();
        },
      };
    })();
    search = s;
    leagueSearch = s;
    s.promise.then(
      (link) => {
        if (search !== s) {
          link.leave();
          return;
        }
        search = null;
        leagueSearch = null;
        const mm = get().matchmaking;
        set({ match: onlineMatch(link), matchmaking: { status: 'found', since, vsBot: link.info.vsBot, league: mm?.league } });
      },
      (err: unknown) => {
        if (search !== s) return;
        search = null;
        leagueSearch = null;
        diag.error(`league: lobby failed: ${String(err)}`);
        const mm = get().matchmaking;
        const reason = err instanceof Error ? err.message : 'failed';
        if (mm?.league) set({ matchmaking: { ...mm, league: { ...mm.league, failed: reason } } });
      },
    );
  },

  fightPlanNow: () => leagueSearch?.fightPlan(),

  watchReplay: async (fightId, day) => {
    try {
      const { fetchReplay } = await import('../net/leagueApi');
      const replay = await fetchReplay(fightId);
      const [a, b] = replay.match.fighters;
      const fighter = (f: typeof a) => ({ fighterId: f.fighterId, name: f.name, ranks: f.ranks, level: f.level, country: f.country, look: f.look });
      // Keeps the league screen's place (target and trail) to come back to.
      set({
        match: { seed: replay.match.seed, arenaId: replay.match.arenaId, player: fighter(a), opponent: fighter(b), local: 0, watch: { ...replay, day } },
        screen: 'fight',
      });
      return true;
    } catch (err) {
      diag.error(`league: replay failed: ${String(err)}`);
      return false;
    }
  },

  stopWatching: () => set({ match: null, screen: 'league' }),

  cancelMatchmaking: () => {
    search?.cancel();
    search = null;
    leagueSearch = null;
    get().match?.online?.leave();
    set({ match: null, matchmaking: null, screen: 'home' });
  },

  finishFight: (match, fightResult, options = {}) => {
    // A league fight changed the table: load it again.
    if (match.online?.info.league) void import('./league').then(({ useLeague }) => useLeague.getState().load(true));
    // From here on, side 0 is this player (online they may have been side 1).
    const result = match.local === 0 ? fightResult : mirrorResult(fightResult);
    const outcome = result.winner === null ? 'draw' : result.winner === 0 ? 'win' : 'loss';
    const summary = { match, result, outcome, connectionLost: options.connectionLost, net: options.net } as const;
    if (serverProgress()) {
      // The server rewarded it (Milestone 4); offline it was practice (D26).
      const server = match.online ? options.server : null;
      if (server) {
        progressStore.save(server.progress);
        set({
          progress: server.progress,
          lastFight: { ...summary, rewards: server.rewards },
          popups: popupsFor(server.rewards, server.progress, isGuest(get().account)),
          screen: 'result',
        });
      } else {
        set({ lastFight: { ...summary, rewards: null, practice: !match.online }, popups: [], screen: 'result' });
        // Whatever the server applied shows up with the next sync.
        if (match.online) void get().syncProgress();
      }
      return;
    }
    const online = match.online?.info;
    const applied = applyFight(
      get().progress,
      // The KO bonus is the winner's; offline fights are always against a bot (M1).
      {
        outcome,
        ko: result.method === 'ko' && outcome === 'win',
        vsBot: !online || online.vsBot,
        ratingDiff: online?.ratingDiff ?? 0,
      },
      Date.now(),
      timeZone(),
    );
    progressStore.save(applied.progress);
    set({
      progress: applied.progress,
      lastFight: { ...summary, rewards: applied.rewards },
      popups: popupsFor(applied.rewards, applied.progress, isGuest(get().account)),
      screen: 'result',
    });
  },

  confirmTraining: async (ranks) => {
    if (serverProgress()) return remoteChange((api) => api.confirmTrainingRemote(ranks));
    const next = confirmTraining(get().progress, ranks);
    if (typeof next === 'string') return false;
    progressStore.save(next);
    set({ progress: next });
    return true;
  },

  resetSkills: async () => {
    if (serverProgress()) return remoteChange((api) => api.resetSkillsRemote());
    const next = resetSkills(get().progress, Date.now());
    if (typeof next === 'string') return false;
    progressStore.save(next);
    set({ progress: next });
    return true;
  },

  buyItem: async (kind, id) => {
    if (serverProgress()) return remoteChange((api) => api.buyCosmeticRemote(kind, id));
    // Local progression (dev builds) has no league, so no Legend unlock.
    const next = buyItem(get().progress, kind, id, { legend: false });
    if (typeof next === 'string') return false;
    progressStore.save(next);
    set({ progress: next });
    return true;
  },

  equipItem: async (kind, id) => {
    if (serverProgress()) return remoteChange((api) => api.equipCosmeticRemote(kind, id));
    const next = equipItem(get().progress, kind, id);
    if (typeof next === 'string') return false;
    progressStore.save(next);
    set({ progress: next });
    return true;
  },

  buyFighter: async (id) => {
    if (serverProgress()) {
      if (!(await remoteChange((api) => api.buyFighterRemote(id)))) return false;
    } else {
      const next = buyFighter(get().progress, id);
      if (typeof next === 'string') return false;
      progressStore.save(next);
      set({ progress: next });
    }
    // Bought either way; if picking it fails, it's a tap away in Profile.
    await get().pickFighter(id);
    return true;
  },

  pickName: (name) => pick('name', { playerName: name }),
  pickFighter: (id) => pick('fighter', { fighterId: id }),

  updateSettings: (patch) => setLocalSettings(patch),

  dismissPopup: () => set({ popups: get().popups.slice(1) }),

  resetProgress: async () => {
    if (serverProgress()) {
      const ok = await remoteChange((api) => api.resetProgressRemote(timeZone()));
      if (ok) set({ lastFight: null });
      return ok;
    }
    progressStore.clear();
    // The Locker stays (D46).
    const progress = keepLocker(get().progress, newProgress(Date.now(), timeZone()));
    progressStore.save(progress);
    set({ progress, lastFight: null });
    return true;
  },

  homeOpened: () => {
    const { progress } = get();
    const next = rollover(progress, Date.now(), timeZone());
    if (next.weekKey === progress.weekKey && next.dayKey === progress.dayKey) return;
    progressStore.save(next);
    set({ progress: next });
  },

  ensurePlayerName: (name) => {
    // Made up, not picked: never sent as a pick, so an account's own name is never replaced by it.
    if (!get().settings.playerName) setLocalSettings({ playerName: name });
  },
}));
